import React, { useState } from 'react';
import { Sparkle, ArrowLeft, Link as LinkIcon, Camera } from '@phosphor-icons/react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { ImageUpload } from './ImageUpload';
import { CostPreviewLine } from '../credits/CostPreviewLine';
import { PaywallModal } from '../credits/PaywallModal';
import { cn, getSession, saveOffer, markApplicationSent, StoredOffer } from '../../lib/utils';
import { getCredits, consumeCredit } from '../../lib/credits';
import { PAGE_ROUTE } from '../../lib/routes';
interface LandlordApplicationFormProps {
  tenantProfileId: string;
  tenantName: string;
  headerless?: boolean;
  onBack?: () => void;
  onSuccess?: () => void;
  onViewOffers?: () => void;
  navigate?: (page: string, context?: any) => void;
  selectedFacilities?: {
    location: string[];
    essential: string[];
    preferred: string[];
    totalAvailable: number;
  };
}
type ApplicationMode = 'quick' | 'custom';
type ContactMethod = 'email' | 'phone' | 'listing';
type PresentationMode = 'link' | 'photos';
const inputCx = 'bg-dark-3 border-dark-4 text-light-1 placeholder:text-light-4 focus-visible:ring-lime-1';
export function LandlordApplicationForm({
  tenantProfileId,
  tenantName,
  headerless = false,
  onBack,
  onSuccess,
  onViewOffers,
  navigate,
  selectedFacilities
}: LandlordApplicationFormProps) {
  const [mode, setMode] = useState<ApplicationMode>('quick');
  const [contactMethod, setContactMethod] = useState<ContactMethod>('email');
  const [presentationMode, setPresentationMode] = useState<PresentationMode>('link');
  const [linkValue, setLinkValue] = useState('');
  const [photos, setPhotos] = useState<File[] | string[]>([]);
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [balance, setBalance] = useState<number>(() => getCredits());
  const [paywallOpen, setPaywallOpen] = useState(false);
  const session = getSession();
  const isSupplyUser = session?.role === 'supply';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Credit gate (supply only). If balance=0, intercept and open paywall.
    if (isSupplyUser && balance <= 0) {
      setPaywallOpen(true);
      return;
    }
    setIsSubmitting(true);
    setTimeout(() => {
      if (session && session.email) {
        // Consume 1 credit BEFORE persisting the offer so the header pill stays
        // accurate even if persistence fails downstream.
        if (isSupplyUser) {
          const newBalance = consumeCredit(`offer_sent:${tenantProfileId}`);
          if (newBalance === null) {
            // Race condition — credits drained between mount and submit.
            setIsSubmitting(false);
            setPaywallOpen(true);
            return;
          }
          setBalance(newBalance);
          // Tell the Header pill to re-read.
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('izio:credits-changed'));
          }
        }
        // Calculate match score
        let matchScore = undefined;
        let matchedFeatures: string[] = [];
        if (selectedFacilities && selectedFacilities.totalAvailable > 0) {
          matchedFeatures = [...selectedFacilities.location, ...selectedFacilities.essential, ...selectedFacilities.preferred];
          matchScore = `${matchedFeatures.length}/${selectedFacilities.totalAvailable}`;
        }
        const offer: StoredOffer = {
          id: `offer_${Date.now()}`,
          status: 'new',
          fromEmail: session.email,
          fromName: session.name,
          fromAvatar: session.avatar,
          toSlug: tenantProfileId,
          toName: tenantName,
          sentAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          message: mode === 'quick' ? 'Am o proprietate care s-ar putea potrivi.' : message,
          contactEmail: mode === 'custom' ? contactEmail : undefined,
          contactPhone: mode === 'custom' ? contactPhone : undefined,
          matchScore,
          matchedFeatures,
          listingPreview: presentationMode === 'link' || mode === 'quick' ? {
            title: 'Proprietate propusă',
            priceLabel: 'Vezi anunț',
            locationLabel: 'București',
            url: linkValue || 'https://izio.ro'
          } : undefined,
          photos: presentationMode === 'photos' && photos.length > 0 ? ['https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400'] : undefined
        };
        saveOffer(offer);
        markApplicationSent(tenantProfileId);
      }
      setIsSubmitting(false);
      setIsSuccess(true);
      onSuccess?.();
    }, 1500);
  };
  const handlePresentationModeChange = (newMode: PresentationMode) => {
    setPresentationMode(newMode);
    // Clear the other field to avoid stale state
    if (newMode === 'link') {
      setPhotos([]);
    } else {
      setLinkValue('');
    }
  };
  if (isSuccess) {
    return <div className="text-center py-12 flex flex-col items-center" data-id="element-1329">
        <Sparkle size={64} weight="fill" className="text-lime-1 mb-4" data-id="element-1330" />
        <h3 className="text-xl font-semibold text-light-1 mb-2" data-id="element-1331">
          Ofertă trimisă!
        </h3>
        <p className="text-light-2 mb-2" data-id="element-1332">
          {tenantName} a fost notificat/ă. Vei primi un răspuns în curând pe
          email.
        </p>
        {isSupplyUser && <p className="text-sm text-light-3 mb-8" data-id="element-1333">
            Mai ai <span className="font-semibold text-lime-1" data-id="element-1334">{balance}</span>{' '}
            {balance === 1 ? 'credit' : 'credite'} în cont.
          </p>}
        {onViewOffers && <Button onClick={onViewOffers} className="bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold h-11 px-8" data-id="element-1335">
            Vezi în Oferte trimise
          </Button>}
      </div>;
  }
  const phoneRequired = contactMethod === 'phone';
  const ContactMethodSelector = <div className="space-y-2" data-id="element-1336">
      <label className="text-sm font-medium text-light-2" data-id="element-1337">
        Metodă preferată de contact
      </label>
      <div className="grid grid-cols-3 gap-2 bg-dark-3 border border-dark-4 rounded-full p-1" data-id="element-1338">
        {([{
        value: 'email',
        label: 'Email'
      }, {
        value: 'phone',
        label: 'Telefon'
      }, {
        value: 'listing',
        label: 'Prin anunț'
      }] as const).map(opt => <button key={opt.value} type="button" onClick={() => setContactMethod(opt.value)} className={cn('h-9 rounded-full text-sm font-medium transition-colors', contactMethod === opt.value ? 'bg-dark-4 text-light-1' : 'text-light-3 hover:text-light-2')} aria-pressed={contactMethod === opt.value} data-id="element-1339">
            {opt.label}
          </button>)}
      </div>
      <p className="text-xs text-light-3" data-id="element-1340">
        Câmpul ales devine obligatoriu. Email-ul e folosit pentru confirmarea
        aplicării.
      </p>
    </div>;
  const outOfCredits = isSupplyUser && balance <= 0;
  const submitLabel = outOfCredits ? 'Cumpără credite pentru a trimite' : isSubmitting ? 'Se trimite...' : mode === 'quick' ? 'Trimite oferta rapidă' : 'Trimite oferta personalizată';
  return <div data-id="element-1341">
      {!headerless && <h3 className="text-xl font-semibold text-light-1 mb-6" data-id="element-1342">
          Trimite ofertă către {tenantName}
        </h3>}

      {/* Tabs */}
      <div className="flex items-center gap-6 border-b border-dark-4 mb-6" data-id="element-1343">
        <button type="button" onClick={() => setMode('quick')} className={cn('pb-3 text-sm font-medium transition-colors relative', mode === 'quick' ? 'text-light-1' : 'text-light-3 hover:text-light-2')} data-id="element-1344">
          Aplicare rapidă
          {mode === 'quick' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-lime-1 rounded-t-full" data-id="element-1345" />}
        </button>
        <button type="button" onClick={() => setMode('custom')} className={cn('pb-3 text-sm font-medium transition-colors relative', mode === 'custom' ? 'text-light-1' : 'text-light-3 hover:text-light-2')} data-id="element-1346">
          Aplicare personalizată
          {mode === 'custom' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-lime-1 rounded-t-full" data-id="element-1347" />}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1348">
        {mode === 'quick' ? <div className="space-y-4 animate-in fade-in duration-300" data-id="element-1349">
            <div className="space-y-2" data-id="element-1350">
              <label className="text-sm font-medium text-light-2" data-id="element-1351">
                Link către anunțul proprietății
              </label>
              <Input type="url" placeholder="https://..." required className={inputCx} data-id="element-1352" />
              <p className="text-xs text-light-3" data-id="element-1353">
                Aplicarea rapidă trimite chiriașului doar link-ul către anunț.
                Datele de contact se găsesc în anunț.
              </p>
            </div>
          </div> : <div className="space-y-4 animate-in fade-in duration-300" data-id="element-1354">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1355">
              <div className="space-y-2" data-id="element-1356">
                <label className="text-sm font-medium text-light-2" data-id="element-1357">
                  Chirie lunară (€)
                </label>
                <Input type="number" placeholder="650" required className={inputCx} data-id="element-1358" />
              </div>
              <div className="space-y-2" data-id="element-1359">
                <label className="text-sm font-medium text-light-2" data-id="element-1360">
                  Garanție (€)
                </label>
                <Input type="number" placeholder="650" required className={inputCx} data-id="element-1361" />
              </div>
            </div>

            {ContactMethodSelector}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1362">
              <div className="space-y-2" data-id="element-1363">
                <label className="text-sm font-medium text-light-2" data-id="element-1364">
                  Email contact
                </label>
                <Input type="email" placeholder="adresa@email.com" required className={inputCx} data-id="element-1365" />
              </div>
              <div className="space-y-2" data-id="element-1366">
                <label className="text-sm font-medium text-light-2" data-id="element-1367">
                  Telefon contact{' '}
                  {!phoneRequired && <span className="text-light-4 font-normal" data-id="element-1368">(opțional)</span>}
                </label>
                <Input type="tel" placeholder="07XX XXX XXX" required={phoneRequired} className={inputCx} data-id="element-1369" />
              </div>
            </div>

            {/* Presentation Mode: Link XOR Photos */}
            <div className="space-y-2" data-id="element-1370">
              <label className="text-sm font-medium text-light-2" data-id="element-1371">
                Cum prezinți proprietatea?
              </label>
              <div className="grid grid-cols-2 gap-2 bg-dark-3 border border-dark-4 rounded-full p-1" data-id="element-1372">
                <button type="button" onClick={() => handlePresentationModeChange('link')} className={cn('h-9 rounded-full text-sm font-medium transition-colors inline-flex items-center justify-center gap-2', presentationMode === 'link' ? 'bg-dark-4 text-light-1' : 'text-light-3 hover:text-light-2')} aria-pressed={presentationMode === 'link'} data-id="element-1373">
                  <LinkIcon size={14} weight="bold" data-id="element-1374" />
                  Link anunț
                </button>
                <button type="button" onClick={() => handlePresentationModeChange('photos')} className={cn('h-9 rounded-full text-sm font-medium transition-colors inline-flex items-center justify-center gap-2', presentationMode === 'photos' ? 'bg-dark-4 text-light-1' : 'text-light-3 hover:text-light-2')} aria-pressed={presentationMode === 'photos'} data-id="element-1375">
                  <Camera size={14} weight="bold" data-id="element-1376" />
                  Încarcă poze
                </button>
              </div>
              <p className="text-xs text-light-3" data-id="element-1377">
                Recomandăm linkul către un anunț online (OLX, Storia,
                Imobiliare). Folosește pozele doar dacă proprietatea nu e
                listată încă.
              </p>
            </div>

            {/* Conditional: Link OR Photos */}
            {presentationMode === 'link' ? <div className="space-y-2 animate-in fade-in duration-200" data-id="element-1378">
                <label className="text-sm font-medium text-light-2" data-id="element-1379">
                  Link către anunț
                </label>
                <Input type="url" placeholder="https://www.olx.ro/..." required value={linkValue} onChange={e => setLinkValue(e.target.value)} className={inputCx} data-id="element-1380" />
              </div> : <div className="space-y-2 animate-in fade-in duration-200" data-id="element-1381">
                <label className="text-sm font-medium text-light-2" data-id="element-1382">
                  Adaugă poze cu proprietatea
                </label>
                <ImageUpload onChange={files => setPhotos(files as any)} data-id="element-1383" />
              </div>}

            <div className="space-y-2" data-id="element-1384">
              <label className="text-sm font-medium text-light-2" data-id="element-1385">
                Mesaj pentru chiriaș
              </label>
              <Textarea placeholder="Descrie pe scurt proprietatea și de ce crezi că se potrivește..." required rows={5} value={message} onChange={e => setMessage(e.target.value)} className={inputCx} data-id="element-1386" />
            </div>
          </div>}

        {/* Terms Checkbox */}
        <div className="flex items-start gap-3 pt-2" data-id="element-1387">
          <input type="checkbox" id="terms" required className="mt-1 w-4 h-4 rounded border-dark-4 bg-dark-3 accent-lime-1 focus:ring-lime-1 focus:ring-offset-dark-2" data-id="element-1388" />
          <label htmlFor="terms" className="text-sm text-light-3 leading-relaxed" data-id="element-1389">
            Sunt de acord cu{' '}
            <a href="/termeni" className="text-light-2 hover:text-light-1 underline underline-offset-2" data-id="element-1390">
              Termenii și Condițiile
            </a>{' '}
            platformei și confirm că datele introduse sunt corecte.
          </label>
        </div>

        {/* Cost preview line — supply only */}
        {isSupplyUser && <CostPreviewLine balance={balance} data-id="element-1391" />}

        {/* Action footer */}
        <div className={cn('flex items-center gap-3 pt-2', onBack ? 'justify-between' : '')} data-id="element-1392">
          {onBack && <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-light-3 hover:text-light-1 transition-colors px-2 h-12" data-id="element-1393">
              <ArrowLeft size={16} weight="bold" data-id="element-1394" />
              Înapoi
            </button>}
          <Button type="submit" className={cn('font-semibold h-12 text-base', outOfCredits ? 'bg-red-3 hover:bg-red-2 text-red-1 border border-red-1' : 'bg-lime-1 hover:bg-lime-2 text-dark-1', onBack ? 'flex-1' : 'w-full')} disabled={isSubmitting} data-id="element-1395">
            {submitLabel}
          </Button>
        </div>
      </form>

      {/* Paywall — opens when supply user with 0 credits attempts submit */}
      <PaywallModal isOpen={paywallOpen} onClose={() => setPaywallOpen(false)} onSeePackages={() => {
      setPaywallOpen(false);
      navigate?.(PAGE_ROUTE.PRICING);
    }} data-id="element-1396" />
    </div>;
}